<template>
    <div class="py-4">
        <div class="flex flex-1 gap-x-2 flex-wrap items-end">
            <div class="w-80">
                <jet-label for="search" value="Visit Type" />
                <multiselect class="multi-sm h-7 text-base"
                    v-model="visitType"
                    :options="visitOptions"
                    placeholder="Select Type"
                    :classes="{singleLabel: 'w-full px-2'}"
                >
                </multiselect>
            </div>
            <div v-if="visitType == 'order'">
                <jet-label for="orderNumber" value="Order #"/>
                <jet-input type="text" class="h-7 text-base" name="orderNumber" v-model="orderNumber" @keydown.enter.prevent v-on:keyup.enter="addLoadOrder()"/>
            </div>
            <div v-if="visitType == 'general'">
                <jet-label for="description" value="Description" />
                <jet-input type="text" class="h-7 text-base" name="description" v-model="description" @keydown.enter.prevent v-on:keyup.enter="addLoadOrder()"/>
            </div>
            <div>
                <jet-tertiary-button type="button" @click="addLoadOrder()">
                    Add
                </jet-tertiary-button>
            </div>
        </div>
        <div v-if="addLoadOrderErrorMsg">
            <jet-input-error :message="addLoadOrderErrorMsg" class="m-1" />
        </div>
    </div>

    <div v-if="load_type == 'delivery'" class="text-sm text-gray-600">
        Drag and drop rows to reorder the visits.
    </div>

    <div class="rounded-t overflow-hidden border border-b-0">
        <table class="table table-with-links w-full">
            <thead>
                <tr>
                    <th v-if="load_type == 'delivery'" class="text-left">Visit #</th>
                    <th class="text-left">Order #</th>
                    <th class="text-left">Customer</th>
                    <th class="text-left">Status</th>
                    <th class="text-left">Printed</th>
                    <th class="hidden md:table-cell text-right">Total</th>
                    <th class="w-min text-center" width="1">Boxes</th>
                    <th class="w-min text-center" width="1">Pallets</th>
                    <th></th>
                </tr>
            </thead>

            <draggable 
                tag="tbody"
                v-model="form.load_orders"
                item-key="index"
                sort="true"
                :disabled="load_type != 'delivery'"
                direction="vertical"
                ghost-class="ghost"
                @start="dragging = true" 
                @end="dragging = false"
            >
                <template #item="{element, index}">
                    <tr
                        :style="{
                            'cursor': load_type != 'delivery' ? null : (dragging ? 'grabbing' : 'grab'),
                        }"
                    >
                        <td v-if="load_type == 'delivery'" class="text-left">
                            <span class="block px-2 py-1">
                                {{ form.load_orders.length - index }}
                            </span>
                        </td>
                        <template v-if="!element.order_id">
                            <td :colspan="load_type == 'delivery' ? 7 : 6" class="text-left">
                                <span class="block px-2 py-1">
                                    {{ element.description }}
                                </span>
                            </td>
                        </template>
                        <template v-else>
                            <td class="text-left">
                                <span class="block px-2 py-1">
                                    {{ element.order.order_number }}
                                </span>
                            </td>
                            <td class="text-left">
                                <span class="block px-2 py-1">
                                    {{ element.order.customer?.name || element.order.shipping_first_name + ' ' + element.order.shipping_last_name }}
                                </span>
                            </td>
                            <td class="text-left">
                                <span class="block px-2 py-1">
                                    {{ element.order.statusDef }}
                                </span>
                            </td>
                            <td class="text-left">
                                <span class="block px-2 py-1">
                                    <img src="/images/icons/check.svg" v-if="element.order.printed_at" width="24" height="24" class="mx-auto" />
                                </span>
                            </td>
                            <td class="hidden md:table-cell text-right">
                                <span class="block px-2 py-1">
                                    {{ toCurrency(total(element.order)) }}
                                </span>
                            </td>
                            <td class="hidden md:table-cell text-right">
                                <span class="block px-2 py-1">
                                    {{ element.order.box_count }}
                                </span>
                            </td>
                            <td class="hidden md:table-cell text-right">
                                <span class="block px-2 py-1">
                                    {{ element.order.pallet_count }}
                                </span>
                            </td>
                        </template>
                        <td class="text-center" style="width: 56px;">
                            <jet-secondary-button type="button" @click="removeLoadOrder(index)">
                                <img src="/images/icons/trash_w.svg" width="16" height="16" alt="Delete" style="min-width: 16px;"/>
                            </jet-secondary-button>
                        </td>
                    </tr>
                </template>
            </draggable>
        </table>
    </div>

    <br>
    <br>
</template>

<script>
import { Link } from '@inertiajs/inertia-vue3';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import Multiselect from '@vueform/multiselect';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError';
import JetLabel from '@/Jetstream/Label';
import draggable from 'vuedraggable'

export default {
    components: {
        JetConfirmationModal,
        JetSecondaryButton,
        JetDangerButton,
        Link,
        JetTertiaryButton,
        Multiselect,
        JetInput,
        JetInputError,
        JetLabel,
        draggable,
    },

    props: ['form', 'load_id', 'load_type'],

    data() {
        return {
            dragging: false,

            addLoadOrderErrorMsg: null,
            visitType: null,
            description: null,
            orderNumber: null,
            
            visitOptions: [
                { value: 'general', label: 'General' },
                { value: 'order', label: 'Order' },
            ],
        }
    },

    methods: {
        subtotal (order) {
            return order.order_items.reduce(function(accumulator, currentValue){
                return accumulator + (currentValue.quantity * currentValue.price);
            }, 0);
        },

        total (order) {
            return this.subtotal(order) + this.tax(order) + this.delivery_fee(order);
        },

        tax (order) {
            if (order.customer && order.customer.tax_exempt) return 0;

            let taxRatio = 0.13;
            let tax = order.order_items.reduce(function(accumulator, currentValue){
                return accumulator + (currentValue.tax === true ?  (currentValue.quantity * currentValue.price * taxRatio) : 0);
            }, 0);

            return tax + (this.delivery_fee(order) * taxRatio);
        },

        delivery_fee(order){
            return order.fee_for_delivery ? parseFloat(order.fee_for_delivery) : 0;
        },

        addLoadOrder () {
            // Validate fields
            if (!this.visitType) return this.addLoadOrderError('A visit type is required.')
            else if (this.visitType == 'order' && !this.orderNumber) return this.addLoadOrderError('An order # is required.')
            else if (this.visitType == 'general' && !this.description) return this.addLoadOrderError('A description is required.')

            if (this.visitType == 'order')
            {
                axios.get(route('loads.get_order', { order_number: this.orderNumber }))
                .then(response => {
                    let order = response.data;

                    if (order == 'fail') this.addLoadOrderError('No order found with order #' + this.orderNumber);
                    else
                    {
                        this.form.load_orders.unshift({
                            load_id: this.load_id,
                            order_id: order.id,
                            order: order,
                        });
                        this.resetAddLoadOrder();
                    }
                });
            }
            else
            {
                this.form.load_orders.unshift({
                    load_id: this.load_id,
                    description: this.description,
                });
                this.resetAddLoadOrder();
            }
        },

        removeLoadOrder (index) {
            this.form.load_orders.splice(index, 1);
        },

        resetAddLoadOrder (visitTypeChange = false) {
            this.addLoadOrderErrorMsg = null;
            if (!visitTypeChange) this.visitType = null;
            this.description = null;
            this.orderNumber = null;
        },

        addLoadOrderError (msg) {
            this.addLoadOrderErrorMsg = msg

            setTimeout(() => {
                this.addLoadOrderErrorMsg = null;
            }, 4 * 1000)

            return false;
        }
    },

    watch: {
        visitType () {
            this.resetAddLoadOrder(true);
        }
    }
}
</script>

<style scoped>
    .ghost
    {
        opacity: 0.5;
        cursor: grabbing;
    }
</style>